import React, { useState, useEffect } from 'react';
import {
    FiSearch, FiPackage, FiMapPin, FiCalendar,
    FiTruck, FiCheckCircle, FiImage, FiDownload, FiX, FiMessageSquare
} from 'react-icons/fi';
import { trackDelivery } from '../../lib/supabase/tracking';
import { useParams } from 'react-router-dom';

const DeliveryTracker = () => {
    const { trackingId: urlTrackingId } = useParams();
    const [trackingId, setTrackingId] = useState(urlTrackingId || '');
    const [tracking, setTracking] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        if (urlTrackingId) {
            handleTrack({ preventDefault: () => {} });
        }
    }, [urlTrackingId]);

    const handleTrack = async (e) => {
        e.preventDefault();
        if (!trackingId.trim()) {
            setError('Please enter a tracking ID');
            return;
        }
        setLoading(true);
        setError(null);
        setTracking(null);
        const { data, error: trackError } = await trackDelivery(trackingId);
        if (trackError || !data) {
            setError('Tracking ID not found. Please check and try again.');
            setLoading(false);
            return;
        }
        setTracking(data);
        setLoading(false);
    };

    const getStatusInfo = (status) => {
        const statusMap = {
            'in_delivery':   { label: 'Out for Delivery', dot: 'bg-blue-500',  text: 'text-blue-700 dark:text-blue-400',  bg: 'bg-blue-50 dark:bg-blue-950/40',  icon: FiTruck },
            'in_transit':    { label: 'Out for Delivery', dot: 'bg-blue-500',  text: 'text-blue-700 dark:text-blue-400',  bg: 'bg-blue-50 dark:bg-blue-950/40',  icon: FiTruck },
            'delivered':     { label: 'Delivered',        dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950/40', icon: FiCheckCircle },
            'in_progress':   { label: 'In Progress',      dot: 'bg-blue-500',  text: 'text-blue-700 dark:text-blue-400',  bg: 'bg-blue-50 dark:bg-blue-950/40',  icon: FiTruck },
            'scheduled':     { label: 'Scheduled',        dot: 'bg-gray-400',  text: 'text-gray-600 dark:text-gray-400',  bg: 'bg-gray-100 dark:bg-zinc-800',     icon: FiCalendar },
            'not_scheduled': { label: 'Not Scheduled',    dot: 'bg-gray-300',  text: 'text-gray-500 dark:text-gray-500',  bg: 'bg-gray-50 dark:bg-zinc-800',      icon: FiPackage },
            'cancelled':     { label: 'Cancelled',        dot: 'bg-gray-300',  text: 'text-gray-500 dark:text-gray-500',  bg: 'bg-gray-50 dark:bg-zinc-800',      icon: FiPackage },
        };
        return statusMap[status] || { label: status, dot: 'bg-gray-300', text: 'text-gray-500', bg: 'bg-gray-50', icon: FiPackage };
    };

    const getOverallStatusLabel = (tracking) => {
        if (!tracking) return '';
        const { overall_status, delivered_count, total_deliveries } = tracking;
        if (overall_status === 'delivered')   return 'All Delivered';
        if (overall_status === 'in_progress') return `${delivered_count}/${total_deliveries} Delivered`;
        if (overall_status === 'scheduled')   return 'Scheduled';
        return 'Not Scheduled';
    };

    const getQuoteTypeLabel = (quoteType) => {
        const labels = { roof: 'Roof', wall: 'Wall', floor: 'Floor', timber: 'Timber' };
        return labels[quoteType] || quoteType;
    };

    const formatDate = (date) => {
        if (!date) return 'Not scheduled';
        return new Date(date).toLocaleDateString('en-AU', {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    const formatDateTime = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('en-AU', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="mobile-public-page tracking-page min-h-screen bg-gray-50 dark:bg-zinc-950">

            {/* Logo bar — anchored to top */}
            <div className="bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800">
                <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col items-center gap-2">
                    <a
                        href="https://notifyapp.com.au"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            className="h-20 w-auto"
                            src={`${process.env.PUBLIC_URL}/assets/wide-logo-black.svg`}
                            alt="Notify"
                        />
                    </a>
                    <p className="text-base mt-4 text-gray-600 dark:text-gray-500 text-center">
                        Custom-built systems for jobs, teams and operations
                    </p>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 py-6 pb-16">

                {/* Page heading */}
                <div className="mb-5">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                        Track Your Delivery
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Enter your tracking ID to check the current status
                    </p>
                </div>

                {/* Search form */}
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-4 mb-5">
                    <form onSubmit={handleTrack} className="flex gap-2">
                        <div className="flex-1 relative">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            <input
                                type="text"
                                value={trackingId}
                                onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                                placeholder="e.g. ABC12345"
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                                maxLength={8}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            {loading ? 'Searching…' : 'Track'}
                        </button>
                    </form>

                    {error && (
                        <p className="mt-3 text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
                            <FiX className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </p>
                    )}
                </div>

                {/* Results */}
                {tracking && (
                    <div className="space-y-4">

                        {/* Job info card */}
                        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
                            <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-0.5">
                                        Tracking ID
                                    </p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white font-mono tracking-widest">
                                        {tracking.short_id}
                                    </p>
                                </div>
                                {(() => {
                                    const s = getStatusInfo(tracking.overall_status);
                                    return (
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 ${s.bg} ${s.text}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
                                            {getOverallStatusLabel(tracking)}
                                        </span>
                                    );
                                })()}
                            </div>

                            <div className="border-t border-gray-100 dark:border-zinc-800 divide-y divide-gray-100 dark:divide-zinc-800 px-5">
                                {tracking.company_name && (
                                    <div className="py-3 flex items-center gap-3">
                                        <FiPackage className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-gray-400">Company</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {tracking.company_name}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {tracking.customer_name && (
                                    <div className="py-3 flex items-center gap-3">
                                        <FiPackage className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-gray-400">Customer</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {tracking.customer_name}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {tracking.customer_address && (
                                    <div className="py-3 flex items-start gap-3">
                                        <FiMapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-400">Job Address</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {tracking.customer_address}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Deliveries */}
                        {tracking.deliveries?.length > 0 && (
                            <>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
                                    Deliveries · {tracking.deliveries.length}
                                </p>

                                {tracking.deliveries.map((delivery, index) => {
                                    const statusInfo = getStatusInfo(delivery.delivery_status);
                                    const deliveryCount = delivery.delivery_count ?? 0;
                                    const ordinalSuffix = deliveryCount === 0
                                        ? ''
                                        : deliveryCount === 1 ? '2nd '
                                        : deliveryCount === 2 ? '3rd '
                                        : `${deliveryCount + 1}th `;
                                    const typeLabel = getQuoteTypeLabel(delivery.quote_type);

                                    // Use the display name ("W1", "W2") from job_quotes.child_unique_id
                                    // Falls back to ordinal labelling for old-system deliveries
                                    const displayName = delivery.child_display_name
                                        ? `${delivery.child_display_name} · ${typeLabel}`
                                        : `${ordinalSuffix}${typeLabel}`;

                                    return (
                                        <div
                                            key={delivery.child_unique_id || `${delivery.quote_type}-${index}-${deliveryCount}`}
                                            className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 overflow-hidden"
                                        >
                                            {/* Header */}
                                            <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="text-xs text-gray-400 mb-0.5">Delivery</p>
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        {displayName}
                                                    </p>
                                                </div>
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${statusInfo.bg} ${statusInfo.text}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusInfo.dot}`} />
                                                    {statusInfo.label}
                                                </span>
                                            </div>

                                            {/* Detail rows */}
                                            <div className="border-t border-gray-100 dark:border-zinc-800 divide-y divide-gray-100 dark:divide-zinc-800 px-5">
                                                {(delivery.expected_delivery_date || delivery.delivery_date) && (
                                                    <div className="py-3 flex items-center gap-3">
                                                        <FiCalendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">
                                                                {delivery.delivery_status === 'delivered' ? 'Delivered On' : 'Expected Delivery'}
                                                            </p>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {formatDate(delivery.delivery_date || delivery.expected_delivery_date)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {delivery.assigned_driver_name && (
                                                    <div className="py-3 flex items-center gap-3">
                                                        <FiTruck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">Driver</p>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {delivery.assigned_driver_name}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {delivery.delivery_address && (
                                                    <div className="py-3 flex items-start gap-3">
                                                        <FiMapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">Delivery Address</p>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {delivery.delivery_address}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {delivery.delivered_at && (
                                                    <div className="py-3 flex items-center gap-3">
                                                        <FiCheckCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">Completed At</p>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {formatDateTime(delivery.delivered_at)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {delivery.delivery_notes && (
                                                    <div className="py-3 flex items-start gap-3">
                                                        <FiMessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">Delivery Notes</p>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white whitespace-pre-wrap">
                                                                {delivery.delivery_notes}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Delivery photos */}
                                            {delivery.delivery_status === 'delivered' && (
                                                <div className="border-t border-gray-100 dark:border-zinc-800 px-5 py-4">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <FiImage className="w-3.5 h-3.5 text-gray-400" />
                                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                                            Delivery Photos
                                                        </span>
                                                        {delivery.photos?.length > 0 && (
                                                            <span className="bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded text-xs font-medium">
                                                                {delivery.photos.length}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {delivery.photos?.length > 0 ? (
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                            {delivery.photos.map((photo) => (
                                                                <div
                                                                    key={photo.id}
                                                                    className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-800 cursor-pointer"
                                                                    onClick={() => setSelectedImage(photo)}
                                                                >
                                                                    <img
                                                                        src={photo.url}
                                                                        alt={photo.original_name || photo.filename}
                                                                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                                                        onError={(e) => {
                                                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50" y="50" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                                                        }}
                                                                    />
                                                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                                                        <p className="text-white text-xs truncate leading-tight">
                                                                            {photo.uploaded_by_name}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-6 bg-gray-50 dark:bg-zinc-800/50 rounded-lg">
                                                            <FiImage className="w-7 h-7 text-gray-300 dark:text-zinc-600 mx-auto mb-1" />
                                                            <p className="text-xs text-gray-400">No photos available</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Image lightbox */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <div
                        className="relative w-full max-w-3xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setSelectedImage(null)}
                            className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors"
                        >
                            <FiX className="w-6 h-6" />
                        </button>
                        <img
                            src={selectedImage.url}
                            alt={selectedImage.filename}
                            className="w-full h-auto rounded-lg"
                        />
                        <div className="mt-3 flex items-center justify-between text-white">
                            <div>
                                <p className="text-sm font-medium">{selectedImage.uploaded_by_name}</p>
                                <p className="text-xs text-white/50">{formatDateTime(selectedImage.uploaded_at)}</p>
                            </div>
                            <a
                                href={selectedImage.url}
                                download={selectedImage.filename}
                                className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm"
                            >
                                <FiDownload className="w-4 h-4" />
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveryTracker;
